import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { Avatar } from '../components/Avatar';
import { VerifiedBadge } from '../components/Badges';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Paper } from '../components/Paper';
import { EmptyState, ErrorNotice, Loading } from '../components/States';
import { useToast } from '../components/Toast';
import { useRefreshBoards } from '../features/feed/queries';
import { FeedList } from '../features/feed/FeedList';
import { BlockDialog } from '../features/posts/BlockDialog';
import { ReportDialog } from '../features/posts/ReportDialog';
import { getProfile, unblockUser } from '../lib/api';
import { errorCode } from '../lib/errors';
import { monthYear } from '../lib/time';
import page from './Page.module.css';
import styles from './ProfilePage.module.css';

export function ProfilePage() {
  const { userId = '' } = useParams();
  const profile = useQuery({ queryKey: ['profile', userId], queryFn: () => getProfile(userId) });
  const [dialog, setDialog] = useState<'report' | 'block' | null>(null);
  const queryClient = useQueryClient();
  const refresh = useRefreshBoards();
  const toast = useToast();

  const unblock = useMutation({
    mutationFn: () => unblockUser(userId),
    onSuccess: async () => {
      toast.show('Unblocked');
      await queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      await refresh();
    },
  });

  if (profile.isPending) return <Loading />;
  if (profile.isError) {
    return errorCode(profile.error) === 'not_found' ? (
      <Paper>
        <EmptyState title="This profile isn't available" />
      </Paper>
    ) : (
      <ErrorNotice error={profile.error} onRetry={() => profile.refetch()} />
    );
  }

  const p = profile.data;
  if (p.isSelf) return <Navigate to="/me" replace />;

  return (
    <div className={page.stack}>
      <Link to="/" className={page.back}>
        <Icon name="back" size={18} />
        Board
      </Link>
      <Paper className={styles.card}>
        <Avatar name={p.displayName} hue={p.avatarHue} size={72} />
        <div>
          <h1 className={styles.name}>{p.displayName}</h1>
          <div className={styles.facts}>
            {p.isNeighbor && <VerifiedBadge />}
            {p.placeName && <span>{p.placeName}</span>}
            <span>Joined {monthYear(p.joinedAt)}</span>
          </div>
        </div>
        {p.bio && <p className={styles.bio}>{p.bio}</p>}
        {!p.isNeighbor && p.isVerified && (
          <p className={`${styles.bio} ${styles.muted}`}>{p.displayName} lives outside your area.</p>
        )}
        <div className={styles.actions}>
          {p.blockedByMe ? (
            <Button size="small" onClick={() => unblock.mutate()} disabled={unblock.isPending}>
              Unblock {p.displayName}
            </Button>
          ) : (
            <Button size="small" variant="quiet" onClick={() => setDialog('block')}>
              <Icon name="block" size={16} />
              Block
            </Button>
          )}
          <Button size="small" variant="quiet" onClick={() => setDialog('report')}>
            <Icon name="flag" size={16} />
            Report
          </Button>
        </div>
      </Paper>

      {!p.blockedByMe && (
        <section className={page.stack} aria-labelledby="recent-notes">
          <h2 id="recent-notes" className={page.sectionTitle}>
            Recent notes on your board
          </h2>
          <FeedList
            filter={{ authorId: p.id }}
            endMessage=""
            empty={<p className={styles.muted}>No notes from {p.displayName} on your board.</p>}
          />
        </section>
      )}

      <ReportDialog
        open={dialog === 'report'}
        onClose={() => setDialog(null)}
        target={{ userId: p.id }}
        subject={p.displayName}
      />
      <BlockDialog
        open={dialog === 'block'}
        onClose={() => setDialog(null)}
        userId={p.id}
        name={p.displayName}
        onBlocked={() => queryClient.invalidateQueries({ queryKey: ['profile', userId] })}
      />
    </div>
  );
}
